import { PayrollPeriodGapNotice } from "./_components/PayrollPeriodGapNotice";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader as LoaderIcon } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { usePermissions } from "@/hooks/usePermissions";
import { useBranchPayrollReadiness } from "@/hooks/useBranchPayrollReadiness";
import { AccessDenied } from "@/components/permission-guard";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { SelectScope, type ScopeItem } from "./_components/SelectScope";
import { SelectionPreview } from "./_components/SelectionPreview";
import { ConfigureRun, type RunConfig } from "./_components/ConfigureRun";
import { RunGenerationProgress } from "./_components/RunGenerationProgress";
import { BranchReadinessNotice } from "./_components/BranchReadinessNotice";
import {
  useListPayrollBranchesQuery,
  useCreateRunMutation,
  useSavePeriodOverrideMutation,
  type ICreateRunRequest,
  type IRunScope,
} from "@/services/(company)/payrollRunsApi";
import { usePayrollRunSocket } from "@/services/websocket/usePayrollRunSocket";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

type Step = 1 | 2 | 3;

const buildScope = (scope: ScopeItem[], t: TFunction): IRunScope => {
  const branchItem = scope.find((s) => s.kind === "branch");
  const branchStringId =
    branchItem?.id ?? scope.find((s) => s.parentBranchId)?.parentBranchId;
  if (!branchStringId)
    throw new Error(t("company:payrolls.no_branch_in_scope"));

  const branchId = Number(branchStringId);

  if (branchItem && scope.length === 1) return { branchId };

  const deptIds = scope
    .filter((s) => s.kind === "department")
    .map((s) => Number(s.id.replace("dept-", "")));
  const groupIds = scope
    .filter((s) => s.kind === "group")
    .map((s) => Number(s.id.replace("grp-", "")));

  return {
    branchId,
    ...(deptIds.length ? { departmentIds: deptIds } : {}),
    ...(groupIds.length ? { payrollGroupIds: groupIds } : {}),
  };
};

export const GeneratePayroll = () => {
  const { t } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(t("company:payrolls.generate_payroll")));
  const navigate = useNavigate();
  const { can, isLoading: isPermissionsLoading } = usePermissions();

  const [step, setStep] = useState<Step>(1);
  const [scope, setScope] = useState<ScopeItem[]>([]);
  const [runConfig, setRunConfig] = useState<RunConfig>({
    period: "",
    payDate: "",
    payrollDate: "",
    name: "",
    notes: "",
  });
  const [activeRunId, setActiveRunId] = useState<number | null>(null);

  const { data: branchesResponse, isLoading: isLoadingBranches } =
    useListPayrollBranchesQuery();
  const branches = branchesResponse?.data ?? [];

  /**
   * Derived above the permission early-returns because `useBranchPayrollReadiness`
   * is a hook and must run on every render.
   */
  const scopeBranchStringId =
    scope.find((s) => s.kind === "branch")?.id ??
    scope.find((s) => s.parentBranchId)?.parentBranchId ??
    null;
  const scopeBranchId = scopeBranchStringId
    ? Number(scopeBranchStringId)
    : null;
  const readiness = useBranchPayrollReadiness(scopeBranchId);

  const [createRun, { isLoading: isCreating }] = useCreateRunMutation();
  const [saveOverride, { isLoading: isSaving }] =
    useSavePeriodOverrideMutation();

  const { progress, items, isGenerationComplete } =
    usePayrollRunSocket(activeRunId);

  useEffect(() => {
    if (isGenerationComplete && activeRunId) {
      navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(activeRunId));
    }
  }, [isGenerationComplete, activeRunId, navigate]);

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (!can(PLATFORM_SECTION_ENUM.PAYROLL, "create")) {
    return (
      <AccessDenied
        description={t(
          "company:payrolls.you_don_t_have_permission_to_generate_payroll_runs",
        )}
        onBack={() => navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT)}
      />
    );
  }

  const activeBranchId = scopeBranchId;
  const scopeBranchName =
    scope.find((s) => s.kind === "branch")?.name ??
    scope.find((s) => s.parentBranchId)?.subtitle ??
    "This branch";

  const handleCancel = () => navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT);

  const handleBack = () => {
    if (step === 3) {
      handleCancel();
      return;
    }
    if (step === 1) handleCancel();
    else setStep((s) => (s - 1) as Step);
  };

  const handleContinue = () => {
    if (step === 1) setStep(2);
  };

  const handleCreateRun = async () => {
    if (!scope.length || !runConfig.period) return;
    const payload: ICreateRunRequest = {
      scope: buildScope(scope, t),
      periodKey: runConfig.period,
      ...(runConfig.payDate ? { payDate: runConfig.payDate } : {}),
      ...(runConfig.payrollDate ? { payrollDate: runConfig.payrollDate } : {}),
      ...(runConfig.name.trim() ? { name: runConfig.name.trim() } : {}),
      ...(runConfig.notes.trim() ? { notes: runConfig.notes.trim() } : {}),
      // No payrollBranchConfigId: the server always uses the config of the
      // branch the employees report to (it overrode the one sent here).
    };
    try {
      const result = await toast.promise(createRun(payload).unwrap(), {
        loading: t("company:payrolls.creating_payroll_run"),
        success: t("company:payrolls.payroll_run_created"),
        error: (err: any) =>
          err?.data?.message ??
          err?.message ??
          t("company:payrolls.failed_to_create_payroll_run"),
      });
      const data = result.data as { id?: number; runId?: number };
      const newRunId = data?.id ?? data?.runId;
      if (!newRunId) {
        toast.error(
          t("company:payrolls.run_created_but_server_did_not_return_a_runid"),
        );
        return;
      }
      setActiveRunId(newRunId);
      setStep(3);
    } catch {
      /* toast.promise already surfaces error */
    }
  };

  /**
   * Save Changes — persists payDate / payrollDate as a per-period override
   * on the branch. Does NOT create a payroll_runs row and does NOT enqueue
   * any generation. The cron picks up the override on its day and creates
   * the run then; or the user can later return and click Create Payroll Run.
   */
  const handleSaveChanges = async () => {
    if (!scope.length || !runConfig.period) return;
    let branchId: number;
    try {
      branchId = buildScope(scope, t).branchId;
    } catch {
      toast.error(t("company:payrolls.select_a_branch_first"));
      return;
    }
    try {
      await toast.promise(
        saveOverride({
          branchId,
          periodKey: runConfig.period,
          ...(runConfig.payDate ? { payDate: runConfig.payDate } : {}),
          ...(runConfig.payrollDate
            ? { payrollDate: runConfig.payrollDate }
            : {}),
          ...(runConfig.name.trim() ? { name: runConfig.name.trim() } : {}),
          ...(runConfig.notes.trim() ? { notes: runConfig.notes.trim() } : {}),
        }).unwrap(),
        {
          loading: t("company:payrolls.saving_changes"),
          success: t("company:payrolls.changes_saved"),
          error: (err: any) =>
            err?.data?.message ??
            err?.message ??
            t("company:payrolls.failed_to_save"),
        },
      );
      navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT);
    } catch {
      /* toast surfaces */
    }
  };

  const stepTitle =
    step === 1
      ? t("company:payrolls.select_payroll_scope")
      : step === 2
        ? t("company:payrolls.configure_payroll_run")
        : t("company:payrolls.generating_payroll");

  const totalScopeEmployees = scope.reduce(
    (sum, s) => sum + (Number(s.employees) || 0),
    0,
  );

  const canCreate = scope.length > 0 && !!runConfig.period && !isCreating;

  return (
    <div className="rounded-xs border border-border bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="border border-surface-elevated rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl:rotate-180" />
          </button>
          <span className="text-sm font-semibold text-gray-900">
            {stepTitle}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          {t("company:employees.step_x_of_y", { current: step, total: 3 })}
        </span>
      </div>

      {/* Body */}
      <div className="px-5 py-5 flex-1 min-h-0">
        {step === 3 ? (
          <RunGenerationProgress progress={progress} items={items} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1px_320px] gap-6 items-stretch">
            <div className="min-w-0">
              {step === 1 && (
                <div className="flex flex-col gap-4">
                  <BranchReadinessNotice
                    branchName={scopeBranchName}
                    readiness={readiness}
                  />
                  <PayrollPeriodGapNotice readiness={readiness} />
                  <SelectScope
                    branches={branches}
                    isLoading={isLoadingBranches}
                    selected={scope}
                    onChange={setScope}
                  />
                </div>
              )}
              {step === 2 && (
                <ConfigureRun
                  branchId={activeBranchId}
                  selected={scope}
                  config={runConfig}
                  onChange={setRunConfig}
                />
              )}
            </div>
            <div className="hidden lg:block bg-border w-px" />
            <SelectionPreview selected={scope} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-border">
        <Button type="button" variant="outline" onClick={handleBack}>
          {t("common:buttons.back")}
        </Button>
        <div className="flex items-center gap-2">
          {step === 1 && (
            <Button
              type="button"
              variant="primary"
              onClick={handleContinue}
              disabled={
                scope.length === 0 ||
                totalScopeEmployees === 0 ||
                readiness.blockingGaps.length > 0
              }
              title={
                readiness.blockingGaps.length > 0
                  ? t(
                      "company:payrolls.branch_missing_required_setting_see_notice",
                    )
                  : undefined
              }
            >
              {t("company:payrolls.continue_to_configure")}
            </Button>
          )}
          {step === 2 && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveChanges}
                disabled={!canCreate || isSaving}
              >
                {isSaving
                  ? t("common:states.saving")
                  : t("company:payrolls.save_changes")}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => handleCreateRun()}
                disabled={!canCreate}
              >
                {isCreating
                  ? t("common:states.creating")
                  : t("company:payrolls.create_payroll_run")}
              </Button>
            </>
          )}
          {step === 3 && (
            <span className="text-xs text-gray-500 self-center">
              {t(
                "company:payrolls.hang_tight_you_ll_be_redirected_to_review_when_generatio",
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratePayroll;
